import { type AppKitNetwork } from "@reown/appkit/networks";
import { createAppKit } from "@reown/appkit/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PropsWithChildren } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter } from "react-router";
import { mainnet, plumeMainnet } from "viem/chains";
import { WagmiProvider } from "wagmi";

import { AnalyticsProvider } from "./analytics-provider";
import { AuthProvider } from "./auth-provider";
import { adapter, GA_TRACKING_ID, queryClient } from "./constants";
import { HomeBannerProvider } from "./home-banner-provider";
import { siweConfig } from "./siwe-config";

import { Provider } from "@/components/ui/provider";
import { viemChains } from "@/data/chains";
import { projectId } from "@/lib/wagmi";

const metadata = {
  name: "Portal Frontend",
  description: "Frontend for the Plume Portal",
  url: "https://portal.plume.org",
  icons: ["/assets/plume-logo-icon.svg"],
};

// Get IDs of predefined chains to avoid duplicates
const predefinedChainIds = new Set<number>([mainnet.id, plumeMainnet.id]);

// Filter out chains that are already defined in viem/chains
const uniqueViemChains = viemChains.filter((chain) => {
  const chainId: number = Number(chain.id);
  return !predefinedChainIds.has(chainId);
});

// Combine predefined chains with converted chains, avoiding duplicates
const allNetworks: [AppKitNetwork, ...AppKitNetwork[]] = [mainnet, plumeMainnet, ...uniqueViemChains] as [
  AppKitNetwork,
  ...AppKitNetwork[],
];

createAppKit({
  adapters: [adapter],
  networks: allNetworks,
  siweConfig,
  projectId,
  metadata,
  features: {
    analytics: true, // Optional - defaults to your Cloud configuration
    socials: false,
    email: false,
  },
  themeVariables: {
    "--w3m-z-index": 100000,
  },
});

export const Providers = ({ children }: PropsWithChildren) => {
  return (
    <ErrorBoundary
      fallback={<p>⚠️ Something went wrong, please try again later</p>}
      onError={(error, errorInfo) => {
        console.error("Error:", error, errorInfo);
      }}
    >
      <WagmiProvider config={adapter.wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <Provider>
                <HomeBannerProvider>
                  <AnalyticsProvider trackingId={GA_TRACKING_ID}>{children}</AnalyticsProvider>
                </HomeBannerProvider>
              </Provider>
              {/* ReactQueryDevtools is automatically excluded from production builds so does not need to be conditionally rendered */}
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </WagmiProvider>
    </ErrorBoundary>
  );
};
